import { configureApi as configureApiGraphql } from "@crowdcoursing/api/graphql";
import { configureApi as configureApiOpenApi } from "@crowdcoursing/api/openapi";
import { Suspense } from "react";
import { RouterProvider } from "react-router-dom";

import { BackendUrlProvider } from "~services/providers/BackendUrlProvider";
import { ReduxProvider, store } from "~services/providers/ReduxProvider";
import { ThemeProvider } from "~services/providers/ThemeProvider";

import { LoginActions, loginWithToken } from "./actions/login";
import ToastNotificationsContainer from "./components/Shared/ToastNotificationsContainer";
import { QueryProvider } from "./services/providers/QueryProvider";
import { browserRouter, memoryRouter } from "./services/router/router";

configureApiOpenApi({
  baseUrl: import.meta.env.REACT_APP_BACKEND_URL,
  headers() {
    return { Authorization: `Bearer ${store.getState().auth.token}` };
  },
  async onResponse(response) {
    if (response.status === 401) {
      store.dispatch(LoginActions.logout());
    }
  },
});

configureApiGraphql({
  baseUrl: import.meta.env.REACT_APP_BACKEND_URL,
  headers() {
    return { Authorization: `Bearer ${store.getState().auth.token}` };
  },
  onRequest(query) {
    const payload = query
      .split("{")[0]
      .split(/(query|mutation)/)[2]
      .trim();

    store.dispatch({ payload, type: "graphql.pending" });
  },
  onResponse(response, query) {
    const payload = query
      .split("{")[0]
      .split(/(query|mutation)/)[2]
      .trim();

    if (response.errors) {
      store.dispatch({ payload, type: "graphql.rejected" });

      if (response.errors?.[0]?.extensions?.code === "AUTH_NOT_AUTHENTICATED") {
        store.dispatch(LoginActions.logout());
      }

      // @ts-expect-error message does not exist here; we will sort this out if it becomes a problem
      throw new Error(response.errors[0]?.message || "(configureGraphQL)(onResponse): Unknown GraphQL Error");
    }

    store.dispatch({ payload, type: "graphql.fulfilled" });
  },
});

// expose a helper for e2e to automatically login
if (store.getState().features.loginAsTestUser) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any)["e2e:login"] = (token: string) => loginWithToken(token, store.dispatch);
}

export interface AppProps {
  useMemoryRouter?: boolean;
}

export const App = ({ useMemoryRouter }: AppProps) => {
  return <RouterProvider router={useMemoryRouter ? memoryRouter : browserRouter} />;
};

const WrappedApp = () => {
  return (
    <BackendUrlProvider>
      <ReduxProvider>
        <QueryProvider>
          <ThemeProvider>
            <ToastNotificationsContainer />
            <Suspense>
              <App />
            </Suspense>
          </ThemeProvider>
        </QueryProvider>
      </ReduxProvider>
    </BackendUrlProvider>
  );
};

export default WrappedApp;
